/* VIYAN Video Showcase: only play the clips that are on screen,
   so a row of autoplay videos doesn't drain battery or bandwidth.
   The section ships each <source> as data-src, so first paint downloads
   only the poster image; the real file is mounted just before the card
   scrolls into view. Browsers block autoplay with sound, so audio starts
   only after the visitor taps a card's speaker button; one clip at a time. */
(() => {
  const SELECTOR = '.viyan-video-showcase video';
  // Start fetching roughly one screen early so playback looks instant.
  const PRELOAD_MARGIN = '600px 0px';

  const buttonFor = (video) => {
    const card = video.closest('.viyan-video-showcase__card');
    return card ? card.querySelector('[data-viyan-vs-sound]') : null;
  };

  const setMuted = (video, muted) => {
    video.muted = muted;
    const button = buttonFor(video);
    if (button) {
      button.classList.toggle('is-unmuted', !muted);
      button.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
    }
  };

  const muteOthers = (current) => {
    document.querySelectorAll(SELECTOR).forEach((video) => {
      if (video !== current && !video.muted) setMuted(video, true);
    });
  };

  // Copy data-src -> src once, then let the element pick the file up.
  const mountSources = (video) => {
    if (video.dataset.viyanVsMounted === 'true') return;
    video.dataset.viyanVsMounted = 'true';
    // The poster ships as data-poster too, so it never competes with the hero image.
    if (video.dataset.poster) {
      video.poster = video.dataset.poster;
      video.removeAttribute('data-poster');
    }
    const pending = video.querySelectorAll('source[data-src]');
    if (pending.length === 0) return;
    pending.forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    video.preload = 'auto';
    video.load();
  };

  const playWhenReady = (video) => {
    mountSources(video);
    video.play().catch(() => {});
  };

  const init = () => {
    const videos = document.querySelectorAll(SELECTOR);
    if (videos.length === 0) return;

    if (!('IntersectionObserver' in window)) {
      videos.forEach(playWhenReady);
      return;
    }

    // Two observers: a wide one that only downloads, a tight one that plays.
    const preloader = new IntersectionObserver(
      (entries, self) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          mountSources(entry.target);
          self.unobserve(entry.target);
        });
      },
      { rootMargin: PRELOAD_MARGIN }
    );

    const player = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            playWhenReady(video);
          } else {
            video.pause();
            // Never resume with surprise audio when scrolled back in.
            if (!video.muted) setMuted(video, true);
          }
        });
      },
      { threshold: 0.25 }
    );

    videos.forEach((video) => {
      preloader.observe(video);
      player.observe(video);
    });
  };

  // Document-level delegation survives theme-editor section re-renders.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-viyan-vs-sound]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    const card = button.closest('.viyan-video-showcase__card');
    const video = card && card.querySelector('video');
    if (!video) return;
    const unmuting = video.muted;
    if (unmuting) muteOthers(video);
    setMuted(video, !unmuting);
    if (unmuting) {
      video.volume = 1;
      playWhenReady(video);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // The theme editor re-renders sections in place; re-attach observers.
  document.addEventListener('shopify:section:load', init);
})();
